import { useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useSearchParams } from 'react-router-dom';

import { useStripeCheckoutSession } from '@client/hooks/useStripeCheckoutSession';
import { selectStripeSubscribeSessionState } from '@client/model/selectors';
import { resetStripeSubscribeSession } from '@client/model/stripeSubscribeSessionModel';
import {
  CHECKOUT_PLAN_CONTENT,
  formatTrialDetail,
  type CheckoutPlanType,
  type CheckoutPlanContent,
} from '@client/features/advisor-onboarding';

export type PlanSelectorViewState = 'plan-selection' | 'checkout';
type PlanType = 'monthly' | 'annual';

const VALID_PLAN_TYPES: CheckoutPlanType[] = ['monthly', 'annual'];

function isValidPlanType(value: string | null): value is CheckoutPlanType {
  return value !== null && VALID_PLAN_TYPES.includes(value as CheckoutPlanType);
}

interface UsePlanSelectorOptions {
  advisorId: string;
}

interface UsePlanSelectorResult {
  viewState: PlanSelectorViewState;
  selectedPlanType: CheckoutPlanType;
  isPending: boolean;
  pendingPlanType: PlanType | null;
  isCheckoutLoading: boolean;
  clientSecret: string | null;
  checkoutContent: CheckoutPlanContent;
  trialDetailFormatted: string | null;
  handleSelectPlan: (planType: PlanType) => void;
  handleBackToPlans: () => void;
}

export function usePlanSelector({ advisorId }: UsePlanSelectorOptions): UsePlanSelectorResult {
  const dispatch = useDispatch();
  const [searchParams, setSearchParams] = useSearchParams();

  const viewParam = searchParams.get('view');
  const planParam = searchParams.get('plan');

  const viewState: PlanSelectorViewState = viewParam === 'checkout' ? 'checkout' : 'plan-selection';
  const selectedPlanType: CheckoutPlanType = isValidPlanType(planParam) ? planParam : 'monthly';

  const { createCheckoutSession, isPending, pendingPlanType } = useStripeCheckoutSession({
    advisorId,
    skipNavigation: true,
  });

  const { clientSecret, isLoading: isCheckoutLoading } = useSelector(
    selectStripeSubscribeSessionState,
  );

  const returnPath = `/advisors/${advisorId}/seats`;

  const handleSelectPlan = useCallback(
    (planType: PlanType) => {
      setSearchParams({ view: 'checkout', plan: planType });
      createCheckoutSession({ planType, returnPath });
    },
    [createCheckoutSession, returnPath, setSearchParams],
  );

  const handleBackToPlans = useCallback(() => {
    dispatch(resetStripeSubscribeSession());
    setSearchParams({}, { replace: true });
  }, [dispatch, setSearchParams]);

  useEffect(() => {
    if (viewState === 'checkout' && !clientSecret && !isCheckoutLoading && !isPending) {
      createCheckoutSession({ planType: selectedPlanType as PlanType, returnPath });
    }
  }, []);

  useEffect(() => {
    if (viewState === 'checkout') {
      document.querySelector('body')?.scrollTo(0, 0);
      window.scrollTo(0, 0);
    }
  }, [viewState]);

  const checkoutContent = CHECKOUT_PLAN_CONTENT[selectedPlanType] ?? CHECKOUT_PLAN_CONTENT.monthly;
  const trialDetailFormatted = checkoutContent.hasTrial
    ? formatTrialDetail(checkoutContent.trialDetail, checkoutContent.trialDays)
    : null;

  const isCheckoutSessionLoading = isPending || isCheckoutLoading;

  return {
    viewState,
    selectedPlanType,
    isPending,
    pendingPlanType: pendingPlanType as PlanType | null,
    isCheckoutLoading: isCheckoutSessionLoading,
    clientSecret: clientSecret ?? null,
    checkoutContent,
    trialDetailFormatted,
    handleSelectPlan,
    handleBackToPlans,
  };
}
